import { z } from 'zod';

import { HhMmSchema, IataSchema, MiceMoneySchema } from './mice';
import { CarrierSchema, FlightNumberSchema } from './mice_refs';
export { CarrierSchema, FlightNumberSchema } from './mice_refs';
import type { MiceNeedAlert, MiceRecommendation } from './mice_needs';

export const MiceFlightHopSchema = z.object({
  carrier: CarrierSchema,
  flightNumber: FlightNumberSchema,
  from: IataSchema,
  to: IataSchema,
  departAt: z.string(),
});
export type MiceFlightHop = z.infer<typeof MiceFlightHopSchema>;

export const MiceToleranceCodeSchema = z.enum([
  'arrival_tolerance',
  'early_departure',
  'last_resort_airport',
  /** Chega na madrugada, até o `redEyeArrivalUntil` do evento: só sem outra opção. */
  'red_eye_arrival',
  'return_departure_tolerance',
  /** Chega na véspera do evento: precisa de mais uma noite. */
  'arrives_day_before',
]);
export type MiceToleranceCode = z.infer<typeof MiceToleranceCodeSchema>;

export const MiceCandidateSchema = z.object({
  id: z.string(),
  direction: z.enum(['OUTWARD', 'RETURN']),
  from: IataSchema,
  to: IataSchema,
  departAt: z.string(),
  arriveAt: z.string(),
  hops: z.array(MiceFlightHopSchema).min(1),
  stops: z.number().int().nonnegative(),
  /** Porta a porta (voos + conexões), do gateway. Ausente quando ele não informa e nos candidatos
   *  salvos antes da duração: nunca calcular pelos horários, que são locais (fusos diferentes). */
  durationMinutes: z.number().int().positive().optional(),
  price: MiceMoneySchema,
  verdict: z.enum(['ideal', 'tolerated']),
  tolerances: z.array(MiceToleranceCodeSchema),
  isSuggestion: z.boolean(),
});
export type MiceCandidate = z.infer<typeof MiceCandidateSchema>;

export const MiceColorSchema = z.enum(['green', 'yellow', 'red']);
export type MiceColor = z.infer<typeof MiceColorSchema>;

export const MiceDayPeriodSchema = z.enum(['morning', 'afternoon', 'evening']);
export type MiceDayPeriod = z.infer<typeof MiceDayPeriodSchema>;

/** Preferência do viajante para UM sentido, já estruturada (o que a planilha dizia em texto livre). */
export const MiceDirectionPreferenceSchema = z.object({
  flight: z.object({ carrier: CarrierSchema, flightNumber: FlightNumberSchema }).nullable(),
  period: MiceDayPeriodSchema.nullable(),
  departAfter: HhMmSchema.nullable(),
  departBefore: HhMmSchema.nullable(),
  carriers: z.array(CarrierSchema),
  /** Aeroportos do viajante pedidos: de onde sair na ida, onde chegar na volta. Fora dos do
   *  cadastro também são buscados ("pelo Rio" com cadastro em CAW). */
  airports: z.array(IataSchema),
  direct: z.boolean(),
  early: z.boolean(),
});
export type MiceDirectionPreference = z.infer<typeof MiceDirectionPreferenceSchema>;

export const EMPTY_DIRECTION_PREFERENCE: MiceDirectionPreference = {
  flight: null,
  period: null,
  departAfter: null,
  departBefore: null,
  carriers: [],
  airports: [],
  direct: false,
  early: false,
};

export const MiceRequestedStatusSchema = z.enum([
  'found',
  'not_requested',
  'not_found',
  'breaks_rules',
]);
export type MiceRequestedStatus = z.infer<typeof MiceRequestedStatusSchema>;

export const MiceDecisionKindSchema = z.enum([
  'kept',
  'swapped_for_price',
  'closest_to_request',
  'preference_match',
  'event_default',
  'operator_choice',
]);
export type MiceDecisionKind = z.infer<typeof MiceDecisionKindSchema>;

export const MiceCheckSchema = z.object({
  key: z.enum([
    'arrival',
    'departure',
    'price',
    'period',
    'carrier',
    'airport',
    'baggage',
    'stops',
  ]),
  state: z.enum(['ok', 'warn', 'bad', 'pref']),
  values: z.record(z.string(), z.string()),
});
export type MiceCheck = z.infer<typeof MiceCheckSchema>;

export const MiceYellowReasonSchema = z.enum([
  'arrives_during_event',
  'leaves_before_end',
  'request_not_found',
  'request_breaks_rules',
  'chosen_breaks_rules',
  'outside_requested_period',
  'tolerance_used',
  'preference_unread',
]);
export type MiceYellowReason = z.infer<typeof MiceYellowReasonSchema>;

/** De onde veio o voo pedido: a planilha do evento ou o texto de preferência da necessidade. */
export const MiceSuggestionSourceSchema = z.enum(['sheet', 'preference']);
export type MiceSuggestionSource = z.infer<typeof MiceSuggestionSourceSchema>;

/** Preço do voo que o viajante pediu, base do saving. `basis` diz de onde o preço saiu: `fare` é a
 *  tarifa já reservada do voo pedido; `search` é o preço da busca. `kept` é se o voo pedido foi o
 *  mantido. */
export const MiceSuggestionPriceSchema = z.object({
  price: MiceMoneySchema,
  basis: z.enum(['fare', 'search']),
  source: MiceSuggestionSourceSchema,
  kept: z.boolean(),
});
export type MiceSuggestionPrice = z.infer<typeof MiceSuggestionPriceSchema>;

/** Como o agente decidiu UM sentido: o pedido do viajante, o que foi escolhido e por quê. */
export const MiceDirectionDecisionSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  requestedStatus: MiceRequestedStatusSchema,
  requested: MiceCandidateSchema.nullable(),
  requestedReasons: z.array(z.string()),
  decision: MiceDecisionKindSchema.nullable(),
  savings: MiceMoneySchema.nullable(),
  swapLimit: MiceMoneySchema.nullable(),
  /** `null` em decisões antigas e quando não dá para saber quem citou o voo. */
  suggestionPrice: MiceSuggestionPriceSchema.nullable().default(null),
  checks: z.object({ requested: z.array(MiceCheckSchema), chosen: z.array(MiceCheckSchema) }),
  eventWindowOption: MiceCandidateSchema.nullable(),
  yellow: z.array(MiceYellowReasonSchema),
  color: MiceColorSchema,
});
export type MiceDirectionDecision = z.infer<typeof MiceDirectionDecisionSchema>;

/** Alertas que pedem olhar do operador; os demais são só informativos. */
export const YELLOW_ALERTS: ReadonlySet<MiceNeedAlert['code']> = new Set([
  'out_of_scope_request',
  'travel_together',
  'existing_voucher',
  'baggage_unknown',
  'airport_outside_sheet',
  'event_airport_changed',
  'airport_request_unmet',
]);

/** Vermelho: sem solução, algum sentido vermelho ou o voo escolhido pelo operador sumiu
 *  (`flight_unavailable`). Amarelo: algum sentido amarelo ou alerta de `YELLOW_ALERTS`. Verde: o
 *  resto. */
export function recommendationColor(
  rec: Pick<MiceRecommendation, 'noSolution' | 'alerts' | 'directions'>,
): MiceColor {
  if (rec.noSolution !== null || rec.directions.some((d) => d.color === 'red')) return 'red';
  if (rec.alerts.some((a) => a.code === 'flight_unavailable')) return 'red';
  if (rec.directions.some((d) => d.color === 'yellow')) return 'yellow';
  if (rec.alerts.some((a) => YELLOW_ALERTS.has(a.code))) return 'yellow';
  return 'green';
}
